import { useCallback, useEffect, useState } from 'react';
import {
  ApiClientError,
  resolveHandleFittingBrand,
  type ApiClientErrorCode,
  type DecorKind,
  type HandleKind,
  type ShopContact,
} from '@pomoshnik/shared';
import { apiClient } from '@/lib/apiClient';
import { ShopsListView, type ShopsListState } from './ShopsListView';
import { CITIES, type ShopMessageKind } from './shopMessage';

/**
 * Empty-текст для decor (ADR-0060 §3): бренд без дилеров в выбранном городе.
 * Переиспользуется для handle, когда бренд РЕЗОЛВИТСЯ (реальный бренд или Аги/Век
 * из «не известно 1/2»), но дилеров нет в городе профиля (ADR-0089 §6.1).
 */
const DECOR_EMPTY_TEXT = 'Дилеров этого бренда в вашем городе пока нет';
/** Empty-текст для furniture (E7, решение Aibek — один текст). */
const FURNITURE_EMPTY_TEXT = 'Магазинов с этой фурнитурой в вашем городе пока нет';
/** Empty-текст для handle БЕЗ резолв-бренда (`resolveHandleFittingBrand=null`, ADR-0089 §6.1). */
const HANDLE_NO_BRAND_EMPTY_TEXT = 'Магазинов в вашем городе пока нет';

/** Чистый путь карточки товара (без query) — для ссылки в запросе дилеру (Фаза 6.1, item 4). */
function cardPath(props: ShopsListProps): string {
  const gid = encodeURIComponent(props.globalId);
  if (props.entityKind === 'handle') return `/handles/${props.subKind}/${gid}`;
  if (props.entityKind === 'decor') return `/decors/${props.subKind}/${gid}`;
  if (props.entityKind === 'furniture') return `/furniture/${gid}`;
  return `/paint/${gid}`;
}

/**
 * Container Экранов «Найти в магазинах» (см. ADR-0005, ADR-0007).
 *
 * Параметризован по `entityKind` + `subKind`:
 * - 'handle' + ('handle'|'hook') — Block 4
 * - 'decor'  + ('facade'|'countertop'|'panel') — Block 2
 * - 'paint'  + 'paint' — Block 3 (заглушка throw в Phase 8)
 *
 * Sensitive endpoint `/api/<entity>/<id>/shops` требует JWT.
 * 401 → soft-gate сообщение «Зарегистрируйтесь, чтобы увидеть контакты»
 * (детально: api-contracts.md §3 «контакты магазинов sensitive»).
 *
 * Имя/код товара берётся отдельным запросом `apiClient[entity].get(...)` —
 * нужно чтобы сгенерировать сообщение для каждой кнопки мессенджера.
 * `productName?` оптимизация (см. B-025) — отложено до Phase 9+.
 *
 * URL params читаются НЕ здесь — в thin wrapper (HandlesShopsRoute /
 * DecorsShopsRoute), который пробрасывает props сюда. Это упрощает unit-тесты
 * (рендер через MemoryRouter с реальным wrapper, не моки useParams).
 */
export type ShopEntityKind = 'handle' | 'decor' | 'paint' | 'furniture';

export type ShopsListProps =
  | {
      entityKind: 'handle';
      subKind: HandleKind;
      globalId: string;
      onBackToCard: () => void;
    }
  | {
      entityKind: 'decor';
      subKind: DecorKind;
      globalId: string;
      onBackToCard: () => void;
    }
  | {
      entityKind: 'paint';
      subKind: 'paint';
      globalId: string;
      onBackToCard: () => void;
    }
  | {
      // furniture (Блок 5, E7) — БЕЗ subKind (единый каталог, ADR-0068).
      entityKind: 'furniture';
      globalId: string;
      onBackToCard: () => void;
    };

/**
 * Маппинг entity context → ShopMessageKind. Container знает context, Presenter
 * получает уже готовый messageKind через props (per ADR-0007).
 */
function toMessageKind(props: ShopsListProps): ShopMessageKind {
  if (props.entityKind === 'handle') return props.subKind;
  if (props.entityKind === 'decor') return `decor-${props.subKind}`;
  if (props.entityKind === 'furniture') return 'furniture';
  return 'paint';
}

interface FetchResult {
  productName: string;
  shops: readonly ShopContact[];
  /** Бренд каталога (verbatim) — слот «<изделие> <название>, <бренд>» (Фаза 6). paint — undefined (RAL). */
  productBrand?: string;
  /**
   * Только handle (ADR-0089 §6.1): резолв-бренд для выбора empty-текста.
   * `null` = подлинно без бренда → «Магазинов в вашем городе пока нет»; иначе
   * (реальный бренд или Аги/Век) → «Дилеров этого бренда в вашем городе пока нет».
   */
  fittingBrand?: string | null;
  /** Подкатегория декора (verbatim) — для спец-ярлыка «Пленка для фасадов» (МДФ
   *  Пленка). Только decor; handles/paint — undefined. */
  productSubcategory?: string;
  /** Только для kind='paint' — обязателен для shopMessage paint-шаблона. */
  ralCode?: string;
}

async function fetchProductAndShops(props: ShopsListProps, city: string): Promise<FetchResult> {
  if (props.entityKind === 'handle') {
    // ADR-0089 §5.3/§7: бэк фильтрует по `city`+бренду ручки (как decor) — шлём
    // выбранный город. `fittingBrand` (резолв того же pure-хелпера, что и бэк) —
    // для выбора empty-текста (§6.1), без второго запроса.
    const [product, shopsRes] = await Promise.all([
      apiClient.handles.get(props.globalId),
      apiClient.handles.shops(props.globalId, city),
    ]);
    return {
      productName: product.name,
      productBrand: product.brand,
      shops: shopsRes.shops,
      fittingBrand: resolveHandleFittingBrand(product),
    };
  }
  if (props.entityKind === 'decor') {
    // ADR-0060 Фаза 5: бэк фильтрует по `city`+бренду — шлём выбранный город.
    const [product, shopsRes] = await Promise.all([
      apiClient.decors.get(props.subKind, props.globalId),
      apiClient.decors.shops(props.subKind, props.globalId, city),
    ]);
    return {
      productName: product.name,
      productBrand: product.brand,
      productSubcategory: product.subcategory,
      shops: shopsRes.shops,
    };
  }
  if (props.entityKind === 'furniture') {
    // furniture (Блок 5, E7): сервер-фильтр city+brand (ci) как decor. productBrand
    // = brandName (гибрид) для слота бренда сообщения. Нет subcategory-ярлыка.
    const [product, shopsRes] = await Promise.all([
      apiClient.furniture.get(props.globalId),
      apiClient.furniture.shops(props.globalId, city),
    ]);
    return {
      productName: product.name,
      productBrand: product.brandName,
      productSubcategory: undefined,
      shops: shopsRes.shops,
    };
  }
  // paint (Block 3 Phase 5) — endpoint без :kind segment, ADR-0010 §6.
  const [product, shopsRes] = await Promise.all([
    apiClient.paint.get(props.globalId),
    apiClient.paint.shops(props.globalId),
  ]);
  // paint: вместо бренда в сообщении — RAL-код (Фаза 6), productBrand не задаём.
  return { productName: product.name, shops: shopsRes.shops, ralCode: product.ralCode };
}

export function ShopsList(props: ShopsListProps) {
  const { globalId } = props;
  // Пикер города ВЕЗДЕ динамический — канон-список из `dealers.cities()` (канон-20,
  // решение Aibek 2026-06-15): и decor/furniture, и handles/paint. Два независимых
  // аспекта «города», раньше слитые в один флаг `usesDealerCities`:
  //  - cityPickerFromDealers: ИСТОЧНИК списка городов = `dealers.cities()` (все kind'ы) —
  //    гейтит начальный `cityOptions`/`selectedCity` и загрузку списка.
  //  - cityIsServerParam: город — серверный параметр фильтра (ADR-0060 Фаза 5 +
  //    ADR-0089 §7: decor/furniture/handle фильтруют на бэке по `city`+бренду).
  //    paint тянет ВСЕ магазины и фильтрует по городу на клиенте → город НЕ в
  //    fetch-deps (без лишних перезапросов при смене города).
  const cityPickerFromDealers =
    props.entityKind === 'decor' ||
    props.entityKind === 'furniture' ||
    props.entityKind === 'handle' ||
    props.entityKind === 'paint';
  const cityIsServerParam =
    props.entityKind === 'decor' ||
    props.entityKind === 'furniture' ||
    props.entityKind === 'handle';
  // subKind есть только у handle/decor/paint; у furniture его нет — для dep-массива.
  const subKindDep = 'subKind' in props ? props.subKind : undefined;
  const [shops, setShops] = useState<readonly ShopContact[] | null>(null);
  const [productName, setProductName] = useState<string>('');
  const [productBrand, setProductBrand] = useState<string | undefined>(undefined);
  // handle empty-текст-различитель (ADR-0089 §6.1): null = подлинно без бренда.
  const [handleFittingBrand, setHandleFittingBrand] = useState<string | null>(null);
  const [productSubcategory, setProductSubcategory] = useState<string | undefined>(undefined);
  const [ralCode, setRalCode] = useState<string | undefined>(undefined);
  const [error, setError] = useState<{ code: ApiClientErrorCode; message: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [cityOptions, setCityOptions] = useState<readonly string[]>(
    cityPickerFromDealers ? [] : CITIES,
  );
  const [selectedCity, setSelectedCity] = useState<string>(cityPickerFromDealers ? '' : 'Астана');
  const [refetchTick, setRefetchTick] = useState(0);

  // Загрузка списка городов пикера (distinct из dealers, канон-порядок) — для ВСЕХ
  // kind'ов (канон-20 везде, решение Aibek 2026-06-15).
  useEffect(() => {
    if (!cityPickerFromDealers) return;
    let cancelled = false;
    apiClient.dealers.cities().then(
      (res) => {
        if (cancelled) return;
        setCityOptions(res.cities);
        setSelectedCity((prev) => prev || res.cities[0] || '');
      },
      (err: unknown) => {
        if (cancelled) return;
        const apiErr =
          err instanceof ApiClientError
            ? { code: err.code, message: err.message }
            : { code: 'INTERNAL_ERROR' as ApiClientErrorCode, message: '' };
        setError(apiErr);
        setLoading(false);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [cityPickerFromDealers]);

  // Ключ рефетча по городу (вынесен из dep-массива — react-hooks/exhaustive-deps
  // не любит сложные выражения в deps):
  //  - decor/furniture/handle (cityIsServerParam): `selectedCity` целиком → рефетч на
  //    КАЖДУЮ смену города (city — серверный параметр фильтра на бэке, ADR-0089 §7);
  //  - paint: город НЕ участвует в fetch (клиентский фильтр), НО первый фетч
  //    обязан дождаться появления города (gate ниже) — поэтому ключ флипается '' → 'ready'
  //    РОВНО один раз, когда `dealers.cities()` задаёт начальный город, и далее НЕ
  //    меняется при смене города (никаких лишних перезапросов — требование Aibek).
  const cityFetchKey = cityIsServerParam ? selectedCity : selectedCity ? 'ready' : '';

  useEffect(() => {
    // Ждём, пока пикер подтянет город (список ещё грузится) — иначе клиентский фильтр
    // мигнёт пустым. Касается всех kind'ов: пикер теперь везде из `dealers.cities()`.
    if (cityPickerFromDealers && !selectedCity) return;
    setLoading(true);
    setError(null);
    let cancelled = false;
    fetchProductAndShops(props, selectedCity).then(
      (result) => {
        if (cancelled) return;
        setProductName(result.productName);
        setProductBrand(result.productBrand);
        setHandleFittingBrand(result.fittingBrand ?? null);
        setProductSubcategory(result.productSubcategory);
        setShops(result.shops);
        setRalCode(result.ralCode);
        setLoading(false);
      },
      (err: unknown) => {
        if (cancelled) return;
        // Не leak-аем String(err) в UI (security audit): non-ApiClientError → пустой message.
        const apiErr =
          err instanceof ApiClientError
            ? { code: err.code, message: err.message }
            : { code: 'INTERNAL_ERROR' as ApiClientErrorCode, message: '' };
        setError(apiErr);
        setLoading(false);
      },
    );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalId, refetchTick, props.entityKind, subKindDep, cityFetchKey]);

  const onRetry = useCallback(() => setRefetchTick((t) => t + 1), []);

  const state: ShopsListState = error ? 'error' : loading ? 'loading' : shops ? 'success' : 'idle';
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const productUrl = origin + cardPath(props);

  return (
    <ShopsListView
      state={state}
      shops={shops ?? undefined}
      error={error ?? undefined}
      productName={productName}
      productBrand={productBrand}
      productSubcategory={productSubcategory}
      productUrl={productUrl}
      productGlobalId={globalId}
      ralCode={ralCode}
      messageKind={toMessageKind(props)}
      selectedCity={selectedCity}
      onCityChange={setSelectedCity}
      cityOptions={cityOptions}
      emptyText={
        props.entityKind === 'decor'
          ? DECOR_EMPTY_TEXT
          : props.entityKind === 'furniture'
            ? FURNITURE_EMPTY_TEXT
            : props.entityKind === 'handle'
              ? // ADR-0089 §6.1: бренд резолвится (вкл. Аги/Век) → decor-текст;
                // подлинно без бренда (resolveHandleFittingBrand=null) → «Магазинов…».
                handleFittingBrand !== null
                ? DECOR_EMPTY_TEXT
                : HANDLE_NO_BRAND_EMPTY_TEXT
              : undefined
      }
      onBackToCard={props.onBackToCard}
      onRetry={onRetry}
    />
  );
}
